import React, { useState } from "react";
import PropTypes from "prop-types";
import { Label, Input, Button, makeStyles, Image, Link } from "@fluentui/react-components";
import config from "../services/config";
import { shorthands } from "@griffel/react";

// we want to create grid layout with CSS Grid
const useStyles = makeStyles({
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    height: "100vh", // Full height of the viewport
  },
  form: {
    marginTop: "24px",
    justifyContent: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    ...shorthands.padding("24px"),
    ...shorthands.gap("10px"),
    position: "relative",
    width: "289px",
    height: "411px",
    backgroundColor: "#FFFFFF",
    ...shorthands.borderRadius("8px"),
  },
  fieldLabel: {
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "20px", // 143% of the font size
    color: "#4D4D4D",
    order: 0,
    alignSelf: "stretch",
    flexGrow: 0,
  },
  logoSection: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    ...shorthands.padding("0px"),
    width: "241px",
    height: "61px",
    ...shorthands.flex("none"),
    order: 0,
    alignSelf: "stretch",
    flexGrow: 0,
  },
  logo: {
    justifySelf: "center", // Center horizontally
    alignSelf: "center", // Center vertically
  },
  titleLabel: {
    justifySelf: "center",
    alignSelf: "center",
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "22px",
    color: "#666666",
    ...shorthands.flex("none"),
    flexGrow: 0,
    marginBottom: "10px",
  },
  emailSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    ...shorthands.padding("0px"),
    ...shorthands.gap("8px"),
    width: "241px",
    height: "96px",
    ...shorthands.flex("none"),
    order: 1,
    alignSelf: "stretch",
    flexGrow: 0,
    marginBottom: "10px",
  },
  emailInput: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    ...shorthands.padding("12px", "16px"),
    width: "241px",
    minWidth: "240px",
    height: "44px",
    backgroundColor: "#FFFFFF",
    ...shorthands.border("1px", "solid", "#569797"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.flex("none"),
    order: 1,
    alignSelf: "stretch",
    flexGrow: 0,
  },
  passwordSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    ...shorthands.padding("0px"),
    ...shorthands.gap("8px"),
    width: "241px",
    height: "72px",
    ...shorthands.flex("none"),
    order: 2,
    alignSelf: "stretch",
    flexGrow: 0,
  },
  submitSection: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    ...shorthands.padding("0px"),
    ...shorthands.gap("16px"),
    width: "241px",
    height: "72px",
    ...shorthands.flex("none"),
    order: 3,
    alignSelf: "stretch",
    flexGrow: 0,
  },
  errorText: {
    width: "111px",
    height: "16px",
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px", // 133% of the font size
    color: "#FF0000",
    ...shorthands.flex("none"),
    order: 2,
    flexGrow: 0,
  },
  passwordForgotSection: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    ...shorthands.padding("0px"),
    width: "241px",
    height: "16px",
    ...shorthands.flex("none"),
    order: 4,
    alignSelf: "stretch",
    flexGrow: 0,
  },
  loginButton: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    ...shorthands.padding("12px"),
    ...shorthands.gap("8px"),
    width: "241px",
    height: "40px",
    backgroundColor: "rgba(53, 124, 124, 0.3)",
    ...shorthands.border("1px", "solid", "#569797"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.flex("none"),
    order: 0,
    alignSelf: "stretch",
    flexGrow: 0,
  },
  forgotPassword: {
    width: "95px",
    height: "16px",
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px", // 138% of the font size
    color: "#0F6CBD",
    flex: "none",
    order: 0,
    flexGrow: 0,
  },
  signup: {
    width: "147px",
    height: "16px",
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px", // identical to box height, or 133%
    textAlign: "right",
    color: "#0F6CBD",
    order: 1,
    flex: "1 0 auto",
  },
  buttonLabel: {
    width: "54px",
    height: "16px",
    fontFamily: "Inter",
    fontStyle: "normal",
    fontWeight: 700,
    fontSize: "16px",
    lineHeight: "100%", // or '16px'
    color: "#4D4D4D",
    ...shorthands.flex("none"),
    order: 0,
    flexGrow: 0,
  },
});

const validateEmail = (email) => {
  const regex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
  return regex.test(email);
};

const LoginForm = ({ onLogin, loginError }) => {
  const [email, setEmail] = useState("");
  const [emailValid, setEmailValid] = useState(false);
  const [password, setPassword] = useState("");

  const handleEmailChange = (e) => {
    const emailInput = e.target.value;
    setEmail(emailInput);
    setEmailValid(validateEmail(emailInput)); // Validate email and update state
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    onLogin(email, password);
  };

  const classes = useStyles();
  return (
    <div className={classes.container}>
      <form className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.logoSection}>
          <Image src="assets/icon-64.png" height={39} width={39} className={classes.logo} />
          <Label className={classes.titleLabel}>Login to Unicli</Label>
        </div>
        <div className={classes.emailSection}>
          <Label className={classes.fieldLabel}> Email: </Label>
          <Input className={classes.emailInput} type="text" value={email} onChange={handleEmailChange} />
          {!emailValid && <Label className={classes.errorText}>Invalid email address</Label>}
        </div>
        <div className={classes.passwordSection}>
          <Label className={classes.fieldLabel}> Password: </Label>
          <Input
            className={classes.emailInput}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className={classes.submitSection}>
          <Button className={classes.loginButton} type="submit" disabled={!emailValid}>
            <Label className={classes.buttonLabel}>Sign In</Label>
          </Button>
          {loginError && <Label className={classes.errorText}>Incorrect email or password</Label>}
        </div>
        <div className={classes.passwordForgotSection}>
          <Link className={classes.forgotPassword} href={config.ForgotPasswordUrl}>
            Forgot password?
          </Link>
          <Link className={classes.signup} href="https://app.unicli.ai/signup/">
            Signup
          </Link>
        </div>
      </form>
    </div>
  );
};

LoginForm.propTypes = {
  onLogin: PropTypes.func.isRequired,
  loginError: PropTypes.bool.isRequired,
};
export default LoginForm;
